import {
  type MultipartInitiateRequest,
  type MultipartInitiateResponse,
  type MultipartCompleteRequest,
  type MultipartCompleteResponse,
  multipartInitiateResponseSchema,
} from "@mg/schemas/src/biggie/upload";

import { UPLOAD_API_URL } from "../config/env";
import { posthogClient } from "../config/posthog";
import { smartFetch } from "../utils/http";

// Types
export interface MultipartUploadInitiateResponse {
  uploadId: string;
  key: string;
  bucket: string;
  parts: {
    partNumber: number;
    uploadUrl: string;
  }[];
}

export interface UploadProgress {
  totalParts: number;
  completedParts: number;
  totalBytes: number;
  uploadedBytes: number;
}

export interface MultipartUploadOptions {
  useAcceleration?: boolean;
  onProgress?: (progress: {
    totalParts: number;
    completedParts: number;
    totalBytes: number;
    uploadedBytes: number;
  }) => void;
}

// Constants
export const DEFAULT_BLOCK_SIZE = 5 * 1024 * 1024; // 5MB

// Utility functions
export function supportsMultipartUpload(): boolean {
  return typeof window !== "undefined" && "Blob" in window;
}

export function createProgressTracker(
  totalParts: number,
  totalBytes: number,
): UploadProgress {
  return {
    totalParts,
    completedParts: 0,
    totalBytes,
    uploadedBytes: 0,
  };
}

// Core multipart upload functions
export async function initiateMultipartUpload(
  filename: string,
  fileSize: number,
  fileType: string,
  useAcceleration = false,
  blockSize = DEFAULT_BLOCK_SIZE,
  isRevisionFile = false,
): Promise<MultipartInitiateResponse> {
  const now = performance.now();

  posthogClient.capture("multipart_upload_initiate_start", {
    filename,
    fileSize,
    fileType,
    useAcceleration,
    blockSize,
    initiatedAt: now,
    isRevisionFile,
  });

  try {
    const json = await smartFetch(`${UPLOAD_API_URL}/multipart/initiate`, {
      method: "POST",
      body: JSON.stringify({
        filename,
        fileSize,
        fileType,
        useAcceleration,
        blockSize,
        isRevisionFile,
      }),
    });

    const parsedData = multipartInitiateResponseSchema.parse(json);
    const newNow = performance.now();

    posthogClient.capture("multipart_upload_initiate_complete", {
      elapsed: newNow - now,
    });

    return parsedData;
  } catch (e) {
    const newNow = performance.now();
    posthogClient.capture("multipart_upload_initiate_error", {
      error: (e as Error).message,
      elapsed: newNow - now,
    });
    throw e;
  }
}

export async function uploadPart(
  partUrl: string,
  partNumber: number,
  chunk: Blob,
  onProgress?: (uploadedBytes: number) => void,
): Promise<{ ETag: string }> {
  const now = performance.now();
  const maxRetries = 3;
  let retryCount = 0;

  posthogClient.capture("part_upload_start", {
    partNumber,
    chunkSize: chunk.size,
    initiatedAt: now,
  });

  while (retryCount < maxRetries) {
    try {
      const response = await fetch(partUrl, {
        method: "PUT",
        body: chunk,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload part ${partNumber}`);
      }

      const etag = response.headers.get("ETag");
      if (!etag) {
        throw new Error(`No ETag received for part ${partNumber}`);
      }

      const newNow = performance.now();
      posthogClient.capture("part_upload_complete", {
        partNumber,
        elapsed: newNow - now,
      });

      if (onProgress) {
        onProgress(chunk.size);
      }

      return { ETag: etag };
    } catch (e) {
      retryCount++;
      if (retryCount === maxRetries) {
        const newNow = performance.now();
        posthogClient.capture("part_upload_error", {
          partNumber,
          error: (e as Error).message,
          elapsed: newNow - now,
        });
        throw e;
      }
      // Wait before retrying (exponential backoff)
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, retryCount) * 1000),
      );
    }
  }

  throw new Error(
    `Failed to upload part ${partNumber} after ${maxRetries} retries`,
  );
}

export async function completeMultipartUpload(
  uploadId: string,
  key: string,
  parts: { partNumber: number; etag: string }[],
): Promise<{
  location: string;
  bucket: string;
  key: string;
  etag: string;
}> {
  const now = performance.now();

  posthogClient.capture("multipart_upload_complete_start", {
    key,
    uploadId,
    partsCount: parts.length,
    initiatedAt: now,
  });

  try {
    const response = await smartFetch(`${UPLOAD_API_URL}/multipart/complete`, {
      method: "POST",
      body: JSON.stringify({
        uploadId,
        key,
        parts,
      }),
    });

    const parsedResponse = response as {
      location: string;
      bucket: string;
      key: string;
      etag: string;
    };
    const newNow = performance.now();

    posthogClient.capture("multipart_upload_complete_finish", {
      elapsed: newNow - now,
    });

    return parsedResponse;
  } catch (e) {
    const newNow = performance.now();
    posthogClient.capture("multipart_upload_complete_error", {
      error: (e as Error).message,
      elapsed: newNow - now,
    });
    throw e;
  }
}

// Main multipart upload function
export async function uploadFileWithMultipart(
  file: File,
  options: MultipartUploadOptions = {},
  isRevisionFile = false,
): Promise<{ key: string; location: string }> {
  if (!supportsMultipartUpload()) {
    throw new Error("Multipart upload is not supported in this environment");
  }

  const { useAcceleration = false, onProgress } = options;
  const fileSize = file.size;
  const blockSize = DEFAULT_BLOCK_SIZE;
  const totalParts = Math.ceil(fileSize / blockSize);

  // Step 1: Initiate multipart upload
  const initiateRequest: MultipartInitiateRequest = {
    body: {
      filename: file.name,
      fileSize,
      fileType: file.type,
      useAcceleration,
      blockSize,
      isRevisionFile,
    },
  };

  const initiateResponse = (await smartFetch(
    `${UPLOAD_API_URL}/multipart/initiate`,
    {
      method: "POST",
      body: JSON.stringify(initiateRequest.body),
    },
  )) as MultipartInitiateResponse;

  const { uploadId, key, parts } = initiateResponse;

  // Step 2: Upload each part
  const uploadedParts: { partNumber: number; etag: string }[] = [];
  let uploadedBytes = 0;

  // Create an array of upload promises
  const uploadPromises = parts.map(async ({ partNumber, uploadUrl }) => {
    const start = (partNumber - 1) * blockSize;
    const end = Math.min(start + blockSize, fileSize);
    const chunk = file.slice(start, end);

    const { ETag: etag } = await uploadPart(
      uploadUrl,
      partNumber,
      chunk,
      (bytes) => {
        uploadedBytes += bytes;
        onProgress?.({
          totalParts,
          completedParts: uploadedParts.length + 1,
          totalBytes: fileSize,
          uploadedBytes,
        });
      },
    );

    return { partNumber, etag };
  });

  // Wait for all uploads to complete and sort by part number
  const results = await Promise.all(uploadPromises);
  uploadedParts.push(...results.sort((a, b) => a.partNumber - b.partNumber));

  // Step 3: Complete multipart upload
  const completeRequest: MultipartCompleteRequest = {
    body: {
      uploadId,
      key,
      parts: uploadedParts,
    },
  };

  const completeResponse = (await smartFetch(
    `${UPLOAD_API_URL}/multipart/complete`,
    {
      method: "POST",
      body: JSON.stringify(completeRequest.body),
    },
  )) as MultipartCompleteResponse;

  return {
    key: completeResponse.key,
    location: completeResponse.location,
  };
}
